import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import { priceDurations } from '../constants/pricing';
import { getOptimizedImageUrl } from '../cloudinaryService';

// ─── Card helpers ───────────────────────────────────────────────────────────

export const isRecentlyPosted = (createdAtTimestamp) => {
  if (!createdAtTimestamp) return false;
  const diffMs = Date.now() - new Date(createdAtTimestamp).getTime();
  return diffMs < 24 * 60 * 60 * 1000; // آخر 24 ساعة
};

export const getCardLink = (listing) => {
  if (listing.adType === 'team_up') return `/teamup/${listing.id}`;
  if (listing.adType === 'profile') return `/profile-ad/${listing.id}`;
  return `/ad/${listing.id}`;
};

export const getCardImage = (listing) => {
  const raw = listing.image || listing.profilePhoto || '';
  return raw ? getOptimizedImageUrl(raw, 'card') : '';
};

export const getCardTitle = (listing, currentLanguage) => {
  if (listing.adType === 'team_up') {
    return listing.personalName || (currentLanguage === 'ar' ? 'تيم أب' : 'Team Up');
  }
  if (listing.adType === 'profile') {
    return listing.name || '';
  }
  return listing.type ? listing.type[currentLanguage] : '';
};

export const getCardLocation = (listing, currentLanguage) =>
  listing.location ? listing.location[currentLanguage] : '';

export const getCardPrice = (listing, currentLanguage) => {
  const currency = currentLanguage === 'ar' ? 'ج.م' : 'EGP';

  // profile ads: budget range
  if (listing.adType === 'profile') {
    const min = listing.budgetMin;
    const max = listing.budgetMax;
    if (min == null && max == null) return '';
    if (min != null && max != null) {
      return `${Number(min).toLocaleString()} – ${Number(max).toLocaleString()} ${currency}`;
    }
    const val = min != null ? min : max;
    return `${Number(val).toLocaleString()} ${currency}`;
  }

  const price = listing.price;
  if (!price) return '';

  const durationObj =
    priceDurations.find((d) => d.value === listing.priceDuration) ||
    priceDurations.find((d) => d.value === 'monthly');

  const perDuration =
    currentLanguage === 'ar'
      ? durationObj?.ar || ''
      : `/${(durationObj?.en || '').toLowerCase()}`;

  return `${Number(price).toLocaleString()} ${currency} ${perDuration}`;
};

// ─── الكارت (قابل لإعادة الاستخدام في الصف والجريد والمفضلة ومنشوراتي) ───

function ListingCard({
  listing,
  currentLanguage,
  getLocalizedText,
  favouriteIds,
  onToggleFavorite,
  isFavorited: isFavoritedProp,
  onDelete,
}) {
  // يدعم: favouriteIds (Set) من ListingsGrid، أو isFavorited (boolean) من MyPosts/Favourites
  const isFavorited = favouriteIds
    ? favouriteIds.has(listing.id)
    : Boolean(isFavoritedProp);

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onToggleFavorite === 'function') {
      onToggleFavorite(listing.id);
    }
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onDelete === 'function') {
      onDelete(listing.id);
    }
  };

  return (
    <Link to={getCardLink(listing)} className="listing-card-new">
      <div className="listing-card-image-wrap">
        {getCardImage(listing) ? (
          <img
            src={getCardImage(listing)}
            alt={getCardTitle(listing, currentLanguage)}
            loading="lazy"
          />
        ) : (
          <div className="listing-card-image-placeholder" />
        )}

        {isRecentlyPosted(listing.createdAtTimestamp) && (
          <span className="listing-card-badge">
            {getLocalizedText('اليوم', 'Today')}
          </span>
        )}

        {typeof onToggleFavorite === 'function' && (
          <button
            type="button"
            className={`listing-card-favorite-btn ${isFavorited ? 'favorited' : ''}`}
            onClick={handleFavoriteClick}
            aria-label="favorite"
          >
            <FontAwesomeIcon icon={isFavorited ? faHeartSolid : faHeartRegular} />
          </button>
        )}

        {typeof onDelete === 'function' && (
          <button
            type="button"
            className="listing-card-delete-btn"
            onClick={handleDeleteClick}
            aria-label="delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}
      </div>

      <div className="listing-card-info">
        <p className="listing-card-title">{getCardTitle(listing, currentLanguage)}</p>
        <p className="listing-card-location">{getCardLocation(listing, currentLanguage)}</p>
        <p className="listing-card-price">{getCardPrice(listing, currentLanguage)}</p>
      </div>
    </Link>
  );
}

export default ListingCard;
